import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, TextField, Typography,
  Alert, CircularProgress, InputAdornment, IconButton
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

export default function FirstLoginPage() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ new_password: '', confirm_password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.new_password !== form.confirm_password) {
      setError('Passwords do not match.');
      return;
    }
    if (form.new_password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post('/auth/first-login-password/', form);
      localStorage.setItem('access', data.access);
      localStorage.setItem('refresh', data.refresh);
      setUser(data.user);
      navigate(data.user.role === 'admin' ? '/admin' : '/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh', background: '#f4f6f4',
      display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2,
    }}>
      <Box sx={{ width: '100%', maxWidth: 420 }}>
        <Box sx={{ background: '#1a4d2e', borderRadius: '12px 12px 0 0', p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ width: 48, height: 48, background: '#2d7a4f', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <LockResetIcon sx={{ color: 'white', fontSize: 26 }} />
          </Box>
          <Box>
            <Typography sx={{ color: 'white', fontWeight: 500, fontSize: 18 }}>Set Your Password</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>You must set a new password to continue</Typography>
          </Box>
        </Box>

        <Box sx={{ background: 'white', borderRadius: '0 0 12px 12px', border: '0.5px solid #e0e8e0', borderTop: 'none', p: 3 }}>
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2, fontSize: 13 }}>
            This is your first login. You must change your password before you can access the system.
          </Alert>

          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

          <form onSubmit={handleSubmit}>
            <TextField
              label="New password"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              margin="normal"
              required
              value={form.new_password}
              onChange={(e) => setForm({ ...form, new_password: e.target.value })}
              helperText="Minimum 8 characters"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                  >
                      {showPassword ? (
                        <VisibilityOffIcon fontSize="small" />
                      ) : (
                        <VisibilityIcon fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
              ),
            }}
          />

            <TextField
              label="Confirm new password"
              type={showConfirmPassword ? 'text' : 'password'}
              fullWidth
              margin="normal"
              required
              value={form.confirm_password}
              onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      size="small"
                    >
                      {showConfirmPassword ? (
                        <VisibilityOffIcon fontSize="small" />
                      ) : (
                        <VisibilityIcon fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading}
              sx={{ mt: 2, background: '#1a4d2e', '&:hover': { background: '#2d7a4f' } }}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : 'Set password & continue'}
            </Button>
          </form>
        </Box>
      </Box>
    </Box>
  );
}